// SidebarRecent — the left menu's recent items in pen's list pattern
// ("Projects and Studio", decision 3; "Left menu – detail design" §1, §3, §4),
// pure.
//
// Names only (no colour marker), starting at the list's left edge. A pinned
// section (only when something is pinned, in pin order) above the recent
// list (the chosen sort, checked in View options). Each section's label names
// its list ("Recent projects"): the menu can hold more
// than one list, so a label that only named the sort would not say which. At most five rows per
// section, then "Show all …" when a destination exists. Compact rows are one
// 13 px line; Detailed rows add the item's meta line. A row's "…" menu, its
// pin and the section's View options button show on hover and focus (pen
// Q82: the pin is its own control at the row's end, filled on a pinned row,
// and "Pin to top" leaves the "…" menu). Each heading is a button with a
// chevron that folds its section (chevron right while folded). A cut name
// scrolls to its end after a second of hover (CSS; none under reduced
// motion), the full name in its tooltip. While loading, skeleton rows in the
// real shape.
//
// Props-driven: the list view (pins, sort, density, folds) comes in; every choice
// reports up as a `RecentListCommand`, every item action through the item.

export type { RecentListProps };
export { recentRowsOf, SidebarRecent, SidebarRecentTiles, useRowMenu };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { LetterTile } from "../LetterTile/LetterTile.js";
import { Menu, type MenuActionItem, type MenuItem } from "../Menu/Menu.js";
import { RowOverflow } from "../RowOverflow/RowOverflow.js";
import { RowRename } from "../RowOverflow/RowRename.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import {
    RECENT_SORTS,
    type RecentListCommand,
    type RecentListView,
    type RecentSection,
    type RecentSort,
    recentSectionsOf,
} from "./recentList.js";
import type { RecentItem } from "./sidebarEntries.js";

type RecentListProps = {
    // Which list: the recent items registered with this `list` id.
    readonly id: string;
    // The author's choices for this list (pins, sort, density).
    readonly view: RecentListView;
    readonly onCommand: (cmd: RecentListCommand) => void;
    // What the two sections are called, shown as their labels and read by
    // screen readers ("Pinned projects", "Recent projects").
    readonly labels: { readonly pinned: string; readonly recent: string };
    // "Show all …" under a full section, only when a destination exists.
    readonly showAll?: { readonly label: string; readonly open: () => void };
    // The items are still being read: skeleton rows.
    readonly loading: boolean;
};

const SKELETON_ROWS = 3;

// The rows one list shows, pins first, as its sections list them: what the
// menu needs to know whether the list shows at all (its divider), and what
// the rail lists as letter tiles.
const recentRowsOf = (
    items: readonly RecentItem[],
    list: RecentListProps,
    locale: string,
): readonly RecentItem[] => {
    const sections = recentSectionsOf(
        items.filter((item) => item.list === list.id),
        list.view,
        locale,
    );
    return [...sections.pinned, ...sections.recent];
};

const useSortLabel = (): ((sort: RecentSort) => string) => {
    const { t } = useLingui();
    return (sort) => {
        switch (sort) {
            case "last-opened":
                return t`Last opened`;
            case "last-changed":
                return t`Last changed`;
            case "name":
                return t`Name A–Z`;
            default:
                return exhaustSwitchCase(sort);
        }
    };
};

// The row's "…" menu (one pattern for the left menu and the Projects page):
// only what works for its kind. On the page, pinned rows read Unpin; in the
// left menu the pin is the row's own control, so the menu leaves it out.
// "Remove from Recent" is the left menu's own, for rows not pinned.
const useRowMenu = (
    onCommand: (cmd: RecentListCommand) => void,
    surface: "sidebar" | "page",
): ((item: RecentItem, pinned: boolean) => readonly MenuItem[]) => {
    const { t } = useLingui();
    return (item, pinned) => {
        const action = (
            label: string,
            icon: IconName,
            onSelect: () => void,
        ): MenuActionItem => ({
            _tag: "item",
            label,
            icon: <Icon name={icon} />,
            onSelect,
        });
        const remove: readonly MenuItem[] =
            item.confirmDelete === undefined
                ? []
                : [
                      { _tag: "separator" } satisfies MenuItem,
                      {
                          ...action(t`Delete …`, "trash", item.confirmDelete),
                          tone: "danger",
                      } satisfies MenuItem,
                  ];
        // An archived item: back from the archive, or deleted for good.
        if (item.restore !== undefined)
            return [action(t`Restore`, "undo", item.restore), ...remove];
        const pin: readonly MenuItem[] =
            surface === "sidebar"
                ? []
                : [
                      pinned
                          ? action(t`Unpin`, "pin", () =>
                                onCommand({ type: "unpin", id: item.id }),
                            )
                          : action(t`Pin to top`, "pin", () =>
                                onCommand({ type: "pin", id: item.id }),
                            ),
                  ];
        return [
            ...pin,
            ...(item.rename === undefined
                ? []
                : [action(t`Rename`, "pencil", item.rename)]),
            ...(item.duplicate === undefined
                ? []
                : [action(t`Duplicate`, "copy", item.duplicate)]),
            ...(pinned || surface === "page"
                ? []
                : [
                      action(t`Remove from Recent`, "eye-off", () =>
                          onCommand({
                              type: "remove-from-recent",
                              id: item.id,
                          }),
                      ),
                  ]),
            ...(item.archive === undefined
                ? []
                : [action(t`Archive`, "archive", item.archive)]),
            ...remove,
        ];
    };
};

const RecentRow = ({
    item,
    pinned,
    detailed,
    menu,
    onCommand,
}: {
    readonly item: RecentItem;
    readonly pinned: boolean;
    readonly detailed: boolean;
    readonly menu: (item: RecentItem, pinned: boolean) => readonly MenuItem[];
    readonly onCommand: (cmd: RecentListCommand) => void;
}): ReactNode => {
    const { t } = useLingui();
    if (item.renaming !== undefined)
        return (
            <div className="bilda-sidebar__recent-row">
                <RowRename
                    initial={item.title}
                    label={t`New name for ${item.title}`}
                    onSubmit={item.renaming.onSubmit}
                    onCancel={item.renaming.onCancel}
                    className="bilda-sidebar__rename"
                />
            </div>
        );
    const meta = detailed ? item.meta : undefined;
    return (
        <div
            className="bilda-sidebar__recent-row bilda-row-overflow-host"
            data-density={detailed ? "detailed" : "compact"}
        >
            <button
                type="button"
                className="bilda-sidebar__recent-item"
                // The full name, also when the row cuts it.
                title={item.title}
                aria-current={item.current ? "true" : undefined}
                data-active={item.current ? "true" : undefined}
                onClick={item.open}
            >
                <span className="bilda-sidebar__recent-title">
                    <span className="bilda-sidebar__recent-name">
                        {item.title}
                    </span>
                </span>
                {meta === undefined ? null : (
                    <span className="bilda-sidebar__recent-meta">{meta}</span>
                )}
            </button>
            <span className="bilda-sidebar__row-actions">
                <RowOverflow
                    label={t`More options for ${item.title}`}
                    items={menu(item, pinned)}
                    menuClassName="bilda-sidebar-menu"
                />
                <Tooltip content={pinned ? t`Unpin` : t`Pin to top`}>
                    <IconButton
                        label={pinned ? t`Unpin` : t`Pin to top`}
                        variant="ghost"
                        size="sm"
                        // Revealed like the "…" (hover, focus).
                        className="bilda-row-overflow__trigger bilda-sidebar__pin"
                        aria-pressed={pinned}
                        onClick={() =>
                            onCommand({
                                type: pinned ? "unpin" : "pin",
                                id: item.id,
                            })
                        }
                    >
                        <Icon name="pin" />
                    </IconButton>
                </Tooltip>
            </span>
        </div>
    );
};

// A section's heading: a button that folds or unfolds its rows, the chevron
// pointing down while open and right while folded.
const SectionHeading = ({
    label,
    title,
    folded,
    onToggle,
}: {
    readonly label: string;
    readonly title?: string;
    readonly folded: boolean;
    readonly onToggle: () => void;
}): ReactNode => (
    <h2 className="bilda-sidebar__heading">
        <button
            type="button"
            className="bilda-sidebar__heading-toggle"
            aria-expanded={!folded}
            title={title}
            onClick={onToggle}
        >
            <span className="bilda-sidebar__heading-text">{label}</span>
            <span className="bilda-sidebar__heading-chevron" aria-hidden="true">
                <Icon
                    name={folded ? "chevron-right" : "chevron-down"}
                    size="xs"
                />
            </span>
        </button>
    </h2>
);

const SidebarRecent = ({
    items,
    list,
}: {
    readonly items: readonly RecentItem[];
    readonly list: RecentListProps;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const sortLabel = useSortLabel();
    const menu = useRowMenu(list.onCommand, "sidebar");
    const { view, onCommand } = list;
    const sections = recentSectionsOf(items, view, i18n.locale);
    const detailed = view.density === "detailed";

    if (list.loading)
        return (
            <section
                className="bilda-sidebar__section"
                aria-label={list.labels.recent}
                aria-busy="true"
            >
                <div className="bilda-sidebar__section-label">
                    <Skeleton width="40%" height="0.5rem" />
                </div>
                {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                    <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: fixed placeholders
                        key={i}
                        className="bilda-sidebar__recent-row"
                        data-density={view.density}
                    >
                        <Skeleton width={`${70 - i * 12}%`} />
                    </div>
                ))}
            </section>
        );

    const viewOptions = (
        <Menu
            align="start"
            contentClassName="bilda-sidebar-menu"
            trigger={
                <IconButton
                    label={t`View options`}
                    variant="ghost"
                    size="sm"
                    // Revealed like a row's "…" (hover, focus, open).
                    className="bilda-row-overflow__trigger bilda-sidebar__view-options"
                >
                    <Icon name="sliders" />
                </IconButton>
            }
            items={[
                { _tag: "label", label: t`Sort by` },
                ...RECENT_SORTS.map(
                    (sort): MenuItem => ({
                        _tag: "item",
                        label: sortLabel(sort),
                        checked: view.sort === sort,
                        onSelect: () => onCommand({ type: "sort-by", sort }),
                    }),
                ),
                { _tag: "separator" },
                { _tag: "label", label: t`View` },
                {
                    _tag: "item",
                    label: t`Compact`,
                    icon: <Icon name="rows-3" />,
                    checked: !detailed,
                    onSelect: () =>
                        onCommand({ type: "show-density", density: "compact" }),
                },
                {
                    _tag: "item",
                    label: t`Detailed`,
                    icon: <Icon name="rows-2" />,
                    checked: detailed,
                    onSelect: () =>
                        onCommand({
                            type: "show-density",
                            density: "detailed",
                        }),
                },
            ]}
        />
    );
    // The View options button sits on the recent label; with everything
    // pinned, on the PINNED label.
    const optionsOnPinned = sections.recent.length === 0;
    const showAll = (more: boolean) =>
        more && list.showAll !== undefined ? (
            <button
                type="button"
                className="bilda-sidebar__show-all"
                onClick={list.showAll.open}
            >
                {list.showAll.label}
            </button>
        ) : null;

    // One section per heading; hidden while it has no rows.
    const section = (
        id: RecentSection,
        rows: readonly RecentItem[],
        more: boolean,
        options: boolean,
    ): ReactNode => {
        if (rows.length === 0) return null;
        const folded = view.folded.includes(id);
        const label = id === "pinned" ? list.labels.pinned : list.labels.recent;
        return (
            <section
                className="bilda-sidebar__section"
                aria-label={label}
                data-section={id}
            >
                <div className="bilda-sidebar__section-label bilda-row-overflow-host">
                    <SectionHeading
                        label={label}
                        title={
                            id === "recent" ? sortLabel(view.sort) : undefined
                        }
                        folded={folded}
                        onToggle={() =>
                            onCommand({
                                type: folded
                                    ? "unfold-section"
                                    : "fold-section",
                                section: id,
                            })
                        }
                    />
                    {options ? viewOptions : null}
                </div>
                {folded ? null : (
                    <>
                        {rows.map((item) => (
                            <RecentRow
                                key={item.id}
                                item={item}
                                pinned={id === "pinned"}
                                detailed={detailed}
                                menu={menu}
                                onCommand={onCommand}
                            />
                        ))}
                        {showAll(more)}
                    </>
                )}
            </section>
        );
    };

    return (
        <>
            {section(
                "pinned",
                sections.pinned,
                sections.morePinned,
                optionsOnPinned,
            )}
            {section("recent", sections.recent, sections.moreRecent, true)}
        </>
    );
};

// The rail's recent items (pen "Shell · Rail", 29 Oct): a neutral letter tile
// per item, the full name in its tooltip (and as its accessible name). A
// click opens the item, as its row in the menu does; the open one is marked.
const SidebarRecentTiles = ({
    items,
}: {
    readonly items: readonly RecentItem[];
}): ReactNode => {
    const { i18n } = useLingui();
    return items.map((item) => (
        <Tooltip key={item.id} content={item.title} side="right">
            <button
                type="button"
                className="bilda-sidebar__tile-row"
                aria-label={item.title}
                aria-current={item.current ? "true" : undefined}
                data-active={item.current ? "true" : undefined}
                onClick={item.open}
            >
                <LetterTile name={item.title} locale={i18n.locale} size="md" />
            </button>
        </Tooltip>
    ));
};
